import { type FormEvent, useRef, useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled, { ThemeProvider } from 'styled-components';
import { Spacing8, Spacing16, Spacing24, Spacing32, Spacing56, Spacing72 } from 'autogen/design-tokens/tokens';
import {
    CreateCustomerNotification,
    type CreateCustomerNotificationSuccess,
    CreateSeveralCustomerNotifications,
    type CreateSeveralCustomerNotificationsSuccess,
    type ProductCustomerNotificationModel
} from 'autogen/swagger/all';
import { SubHeader, Success } from 'autogen/translation-keys/trans-website-preordercustomernotification';
import { Email, HeadLine } from 'autogen/translation-keys/trans-website-productcustomernotification';
import { EmailNotValid } from 'autogen/translation-keys/trans-website-productcustomernotification-error';
import { BackInStockNotification, Preorder } from 'autogen/translation-keys/trans-website-shared-buttons';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { ButtonWithSpinner } from 'components/Button';
import chanelTheme from 'components/chanel/shared/themeStyling';
import HighlightedText from 'components/generic/HighlightedText';
import Image from 'components/generic/Image';
import { ProductNameCompact } from 'components/generic/productName/ProductName';
import { Headline } from 'components/generic/typography';
import TextInput from 'components/shared/inputs/text';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import toast from 'components/Toast';
import { useIsChanel } from 'hooks/user/useIsBrand';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import { breakpoint, sizes } from 'variables';
import { z } from 'zod';
import { useNotificationFormStore } from './NotificationFormStore';

export interface INotificationForm {
    model: ProductCustomerNotificationModel | ProductCustomerNotificationModel[];
    subHeader?: string;
}
// #region Styles
const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${Spacing32};
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${Spacing24} ${Spacing24};
    gap: ${Spacing16};
    flex: 1;
    overflow: auto;
`;

const StyledHighlightedText = styled(HighlightedText)`
    padding: 0;
`;

const InputWrapper = styled.form`
    position: relative;
    display: flex;
    flex-direction: column;
    margin: 0 0 ${Spacing8};
`;

const TopContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding-top: ${Spacing72};
    padding-bottom: ${Spacing56};
    gap: ${Spacing24};

    &:after {
        content: '';

        ${imageOverlayStyle}
    }
`;

const ProductImage = styled(Image.WithScaling)`
    align-self: center;
    max-width: 136px;
    max-height: 136px;

    ${breakpoint.up(sizes.lg)} {
        max-width: 206px;
        max-height: 206px;
    }
`;

// #endregion Styles

class ValidationException extends Error {
    constructor(message) {
        super(message);
        this.message = message;
        this.name = 'ValidationException';
    }
}

function ButtonRenderer({
    isPreorder,
    disabled,
    formSubmit
}: {
    isPreorder: boolean;
    disabled: boolean;
    formSubmit: () => Promise<void>;
}) {
    const { t } = useTranslation();
    const isChanel = useIsChanel();

    return isChanel ? (
        <ThemeProvider theme={chanelTheme as any}>
            <ButtonWithSpinner onClick={formSubmit} size="lg" disabled={disabled}>
                {isPreorder ? t(Preorder) : t(BackInStockNotification)}
            </ButtonWithSpinner>
        </ThemeProvider>
    ) : (
        <ButtonWithSpinner onClick={formSubmit} size="lg" disabled={disabled}>
            {isPreorder ? t(Preorder) : t(BackInStockNotification)}
        </ButtonWithSpinner>
    );
}

function NotificationFormContent(props: INotificationForm): JSX.Element {
    const { model } = props;
    const { isArray } = Array;
    const { t } = useTranslation();

    const formRef = useRef<HTMLFormElement>(null);
    const [emailValue, setEmailValue] = useState<string>(isArray(model) ? model[0]?.email || '' : model.email || '');
    const [emailErrorMessage, setEmailErrorMessage] = useState<FieldError | undefined>();
    const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [confirmMessage, setConfirmMessage] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const setShowFormSuccessMessage = useNotificationFormStore((state) => state.setShowSuccessMessage);
    const setFormConfirmMessage = useNotificationFormStore((state) => state.setConfirmMessage);
    const emailSchema = z.string().email();
    const preorder = isArray(model) ? model[0]?.isPreorder : model.isPreorder;
    const GA4Tracking = useGA4Tracking();

    function checkValidation(value) {
        if (value !== undefined) {
            try {
                emailSchema.parse(value);
                setIsEmailInvalid(false);
                setEmailErrorMessage(undefined);
                return true;
            } catch (error) {
                setIsEmailInvalid(true);
                setEmailErrorMessage({ type: 'invalid_string', message: t(EmailNotValid) });
            }
        }

        return false;
    }

    function handleOnChange(event: FormEvent<HTMLInputElement>) {
        const {
            currentTarget: { value }
        } = event;

        setEmailValue(value);

        if (isDirty) {
            checkValidation(value);
        }
    }

    async function formSubmit(): Promise<void> {
        if (!checkValidation(emailValue)) {
            setIsDirty(true);
            return;
        }

        setIsDirty(false);

        try {
            let response: CreateSeveralCustomerNotificationsSuccess | CreateCustomerNotificationSuccess;

            if (isArray(model)) {
                const productIds = model.map(({ ...product }) => product.productId);

                const notificationModel = {
                    productIds,
                    email: emailValue
                };

                response = await CreateSeveralCustomerNotifications(notificationModel);
            } else {
                const notificationModel = {
                    productId: model.productId,
                    isPreorder: model.isPreorder,
                    email: emailValue
                };

                response = await CreateCustomerNotification(notificationModel);
            }

            const { success, message } = response;

            if (success) {
                GA4Tracking({
                    eventName: 'notification_request',
                    context: 'product_page',
                    category: 'ecommerce',
                    eventParams: [{ param_name: 'notification_type', param_value: 'back_in_stock' }]
                });

                const successMessage = preorder ? t(Success) : message;
                setConfirmMessage(successMessage);

                setShowFormSuccessMessage(true);
                setFormConfirmMessage(successMessage);
            } else if (message) {
                throw new ValidationException(message);
            } else {
                throw new Error('Unknown error');
            }
        } catch (error) {
            console.error(error);

            if (error instanceof ValidationException) {
                // display validation error
                setConfirmMessage(undefined);
                setErrorMessage(error.message);
            } else {
                // display generic error
                toast.error(t(GeneralExceptionError));
            }
        }
    }

    function handleSubmit(event) {
        event.preventDefault();
        formSubmit();
    }

    return (
        <Wrapper>
            {!isArray(model) && model.image && (
                <TopContainer>
                    <ProductImage
                        src={model.image}
                        sizes={[[sizes.xl, 206], 136]}
                        transformType="WidthScale"
                        imageType="product"
                        alt=""
                    />
                </TopContainer>
            )}
            <Container>
                {isArray(model) ? (
                    <Headline size="M">{model[0]?.isPreorder ? t(SubHeader) : t(HeadLine)}</Headline>
                ) : (
                    <>
                        <Headline size="M">{model.isPreorder ? t(SubHeader) : t(HeadLine)}</Headline>
                        <ProductNameCompact productName={model.productName} size="md" />
                    </>
                )}
                <InputWrapper ref={formRef} onSubmit={handleSubmit}>
                    <TextInput
                        name="Email"
                        type="email"
                        autoComplete="email"
                        placeholder={t(Email)}
                        value={emailValue}
                        onChange={(event) => handleOnChange(event)}
                        required
                        minLength={2}
                        maxLength={200}
                        isInvalid={!!emailErrorMessage}
                        helperText={
                            emailErrorMessage?.message
                                ? { message: emailErrorMessage.message, type: 'error' }
                                : undefined
                        }
                    />
                </InputWrapper>
                <ButtonRenderer disabled={isEmailInvalid} formSubmit={formSubmit} isPreorder={preorder} />
                {confirmMessage && (
                    <StyledHighlightedText color="ColorSystemPositive1" withBorder={false}>
                        {confirmMessage}
                    </StyledHighlightedText>
                )}
                {errorMessage && (
                    <StyledHighlightedText color="ColorSystemNegative1" withBorder={false}>
                        {errorMessage}
                    </StyledHighlightedText>
                )}
            </Container>
        </Wrapper>
    );
}

export default function NotificationForm(props: INotificationForm): JSX.Element {
    return <NotificationFormContent {...props} />;
}
